<template>
  <mastery-footer
    v-if="flashcard.id"
    class="flashcard-footer-component-container"
    :disable-btn="disableSubmit"
    :show-btn="!answered"
    :primary-action-text="buttonText"
    @primary-action="submit"
  >
    <template v-slot:before-action>
      <bookmark-button
        v-if="showBookmark"
        class="before-action bookmark-button"
        :class="{ answered: answered }"
        :asset-name="flashcard.question"
        :bookmarkable-type="'Flashcard'"
        :bookmarkable-id="flashcard.id"
        :context-id="category ? category.id : null"
        :show-tooltip="true"
      />
      <flashcard-share-popover
        v-if="sharingEnabled"
        :category="category"
        :context="'footer'"
        :flashcard="flashcard"
      />
    </template>
    <template v-slot:asset-tray-button>
      <button
        v-if="hasAssetTray"
        class="asset-tray-button"
        @click="toggleAssetTray"
      >
        <icon
          v-if="isAssetTrayOpen"
          class="asset-tray-cross-icon"
          name="cross-thin"
          variant="ffffff"
        />
        <icon
          v-else
          class="asset-tray-icon"
          name="asset-tray"
          variant="ffffff"
        />
      </button>
    </template>
    <template v-slot:after-action>
      <div
        v-if="!answered"
        class="after-action"
      >
        <bookmark-button
          v-if="showBookmark"
          class="bookmark-button"
          :class="{ answered: answered }"
          :asset-name="flashcard.question"
          :bookmarkable-type="'Flashcard'"
          :bookmarkable-id="flashcard.id"
          :context-id="category ? category.id : null"
          :show-tooltip="true"
        />
        <button
          v-if="allowSkipping"
          class="skip-button"
          @click="$emit('skip')"
        >
          Skip
        </button>
        <timer
          v-else-if="userCertificationId && flashcard"
          ref="timer"
          :flashcard="flashcard"
        />
      </div>
      <template v-else>
        <key-capture
          @keydown="handleKeyDown"
        ></key-capture>
        <div
          v-if="showFlashcardStatuses"
          class="action-row tags"
          :class="{ answered : answered }"
        >
          <tour-tip :tourtip-name="'tagging'">
            <button
              v-for="status in answeredFlashcardStatuses"
              :key="status.id"
              :class="status.name"
              class="action-button tag-button"
              @click="tag(status.id)"
            >
              <icon
                class="tag-icon"
                name="tag"
                :variant="tagIconVariant(status)"
              /> {{ status.displayName }}
            </button>
          </tour-tip>
        </div>
        <div
          v-else
          class="action-row"
          :class="{ answered : answered }"
        >
          <button
            class="action-button tag-button"
            @click="tag(greenFlashcardStatus.id)"
          >
            Next Question
          </button>
        </div>
      </template>
    </template>
  </mastery-footer>
</template>

<script>
import MasteryFooter from '@components/mastery/footer';
import BookmarkButton from '@components/singletons/bookmarkButton';
import Icon from '@components/singletons/icon';
import KeyCapture from '@/components/singletons/KeyCapture';
import FlashcardSharePopover from '@components/flashcards/flashcard-share-popover';
import Timer from '@components/singletons/timer';
import TourTip from '@components/singletons/tour-tip';

import { hasAnsweredFlashcard } from '@/utils/flashcards';

export default {
  components: { BookmarkButton, MasteryFooter, Icon, FlashcardSharePopover, Timer, TourTip, KeyCapture },
  props: {
    allowSkipping: {
      type: Boolean,
      default: false,
    },
    answered: {
      type: Boolean,
      default: false,
    },
    answerIds: {
      type: Array,
      default: () => [],
    },
    category: {
      type: Object,
    },
    correct: {
      type: Boolean,
      default: false,
    },
    flashcard: {
      type: Object,
    },
    rawAnswerData: {
      type: Object,
    },
    sharingEnabled: {
      type: Boolean,
      default: false,
    },
    showBookmark: {
      type: Boolean,
      default: false,
    },
    showFlashcardStatuses: {
      type: Boolean,
      default: false,
    },
    terminology: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    answeredFlashcardStatuses() {
      return this.$store.getters['flashcardStatuses/answeredFlashcardStatuses'];
    },
    greenFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
    },
    isAssetTrayOpen() {
      return this.$store.getters['ui/showAssetTray'];
    },
    buttonText() {
      if (this.category) {
        const ct = this.$store.getters['categoryTypes/categoryTypes'].find(ct => ct.id === this.category.categoryTypeId);
        if (ct && ct.buttonText) {
          return ct.buttonText;
        }
      }
      const fallbackText = this.terminology ? 'View Definition' : 'Submit';
      return fallbackText;
    },
    disableSubmit() {
      return hasAnsweredFlashcard(this.flashcard, this.answerIds, this.rawAnswerData, this.terminology);
    },
    hasAssetTray() {
      let hasAssetTray = false;

      if (this.flashcard?.assetAssociations?.length) {
        const context = this.answered ? 'rationale' : 'question';
        hasAssetTray = this.checkAssociationsForAssetTray(context);
      }

      return hasAssetTray;
    },
    userCertificationId() {
      if( this.$route ) {
        return this.$route.params.userCertificationId;
      }
    },
  },
  methods: {
    checkAssociationsForAssetTray(context) {
      const assetTrays = this.flashcard?.assetAssociations.filter(assetAssociation => {
        if (assetAssociation?.assetAContext === context) {
          const grouping = this.$store.getters['groupings/all'][assetAssociation?.assetBId];
          return grouping?.groupingType === 'Asset Tray';
        }
      });
      return !!(assetTrays?.length);
    },
    getFlashcardAssetTrayGrouping() {
      const context = this.answered ? 'rationale' : 'question';
      let assetTrayGrouping = null;
      this.flashcard?.assetAssociations.forEach(assetAssociation => {
        if (assetAssociation?.assetAContext === context) {
          const grouping = this.$store.getters['groupings/all'][assetAssociation?.assetBId];
          if (grouping?.groupingType === 'Asset Tray') {
            assetTrayGrouping = grouping;
          }
        }
      });
      return assetTrayGrouping;
    },
    handleKeyDown(key) {
      if (key.length != 1) return;

      if (this.showFlashcardStatuses) {
        const matchingStatus = this.answeredFlashcardStatuses.find(
          status => status.displayName.toUpperCase()[0] === key.toUpperCase()
        );
        if (matchingStatus) {
          this.tag(matchingStatus.id);
        }
      } else {
        if (key.toUpperCase() === 'N') {
          this.tag(this.greenFlashcardStatus.id);
        }
      }
    },
    submit() {
      if (!this.disableSubmit) {
        // timer only present for certifications
        let time = this.$refs.timer ? this.$refs.timer.time : 0;
        this.$emit('answer', time);

        this.$store.commit('ui/setShowAssetTray', false);
        window.scrollTo(0, 0);
      }
    },
    tag(flashcardStatusId) {
      this.$emit('tag', flashcardStatusId);
      this.$emit('nextFlashcard');
    },
    tagIconVariant(status) {
      const tagHexMap = {
        'red': 'ed857e',
        'green': '8fd27a',
        'yellow': 'f9d388',
      };
      return tagHexMap[status.name.toLowerCase()];
    },
    toggleAssetTray() {
      const assetTray = this.getFlashcardAssetTrayGrouping();
      const params = {
        'Parent Asset ID': this.flashcard?.id,
        'Parent Category ID': this.flashcard?.categoryIds[0],
        'Parent Asset Type': 'Flashcard',
        'Asset ID': assetTray?.id,
      };

      if (this.$store.getters['ui/showAssetTray']) {
        this.$analytics.events.viewedAssetTray(params);
      } else {
        this.$analytics.events.closedAssetTray(params);
      }
      this.$store.commit('ui/setShowAssetTray', !this.isAssetTrayOpen);
    },
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-footer-component-container {
    padding: 0 rem-calc(48) !important;
    display: flex;
    flex-direction: row;
    align-items: center;

    @include breakpoint(small) {
      padding: 0 rem-calc(16) !important;

    }

    .before-action {
      height: 100%;
      &.bookmark-button {
        @include breakpoint(medium) {
          display: none;
        }
      }
    }

    .asset-tray-button {
      width: rem-calc(48);
      height: rem-calc(48);
      background: #000000;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      .asset-tray-cross-icon {
        width: rem-calc(19);
        height: rem-calc(19);
      }
      .asset-tray-icon {
        width: rem-calc(29);
        height: rem-calc(23);
      }
    }

    .after-action {
      margin-left: auto;
      display: flex;
      height: 100%;

      .bookmark-button {
        display: none;

        @include breakpoint(medium) {
          height: 100%;
          display: initial;
        }
      }

      .bookmark-button ~ .skip-button {
        margin-left: rem-calc(8);
      }

      .skip-button {
        height: 100%;
        background: transparent;
        padding: 0 rem-calc(8);
        color: #2ea2f8;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .action-row {
      width: rem-calc(592); // 568 (width of flashcard view) + 24 (width of bookmark button)
      margin: 0 auto;

      &.tags.answered {
        @include breakpoint(medium) {
          flex-wrap: wrap;
          justify-content: center;
        }
      }

      .tag-button {
        background-color: #f2f4f7;
        border: 1px solid darken(#f2f4f7, 5%);
        border-radius: 4px;
        color: #354052;
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        line-height: rem-calc(21);
        padding: rem-calc(4) rem-calc(14);
        margin-right: rem-calc(16);

        .tag-icon {
          margin-right: rem-calc(4);
          display: inline-block;
          vertical-align: middle;
        }

        &:hover {
          background-image: linear-gradient(to top, darken(#f2f4f7, 5%), darken(#ffffff, 5%));
        }

        @include breakpoint(medium) {
          font-size: rem-calc(12);
          padding: rem-calc(2) rem-calc(6);
        }

        @include breakpoint(small) {
          margin-right: rem-calc(8);
        }

        // custom breakpoint until we can get tags to fit better on mobile
        @media only screen and (max-width: 360px) {
          font-size: rem-calc(10);
          padding: 0 rem-calc(4);
        }
      }
    }
  }
</style>
